<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveUser">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create user') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('This information will be displayed publicly') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="name"
                                            v-model="user.name"
                                            :placeholder="$t('Name')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="email"
                                            v-model="user.email"
                                            :placeholder="$t('Email')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            type="email"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Emails') }}</label>
                                    <input-email
                                        id="emails"
                                        ref="emails"
                                        :emailList="user.emails"
                                    ></input-email>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="phone">{{ $t('Phone') }}</label>
                                    <input-phone
                                        id="phones"
                                        ref="phones"
                                        :phoneList="user.phones"
                                    ></input-phone>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="profile">{{ $t('Profile') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="profile"
                                            v-model.lazy="user.profile_id"
                                            :options="profileList"
                                            option-label="name"
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="password">{{ $t('Password') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="password"
                                            v-model="user.password"
                                            :placeholder="$t('Password')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                            type="password"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('User settings') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('User access and permission settings') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="role">{{ $t('Role') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="role"
                                            v-model.lazy="user.role_id"
                                            :options="userRoles"
                                            option-label="name"
                                            required
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="center">{{ $t('Center') }}<strong v-show="user.role_id == 3" class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <multiselect
                                            v-model="selectedCenters"
                                            :placeholder="$t('Select an option')"
                                            label="name"
                                            track-by="id"
                                            :options="centerList"
                                            :multiple="true"
                                            :taggable="true"
                                            class="rounded-md"
                                        >
                                        </multiselect>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <input-switch
                                        id="status"
                                        v-model="user.status"
                                        :disabled-label="$t('The user is disabled')"
                                        :enabled-label="$t('The user is activated')"
                                    ></input-switch>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('When the user is deactivated, the registry is created in the system, so the email is reserved, but can not login until it is activated again') }}.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/users"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Create user') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
import Multiselect from 'vue-multiselect'

export default {
    name: "new-user",
    components: { Multiselect },
    metaInfo() {
        return {
            title: this.$i18n.t('Create user')
        }
    },
    data() {
        return {
            userRoles: [],
            user: {
                id: null,
                id_ext: null,
                name: null,
                email: null,
                avatar: null,
                emails: [],
                phones: [],
                role_id: null,
                profile_id: null,
                center_ids: [],
                status: true,
                password: null,
            },
            profileList: [],
            centerList: [],
            selectedCenters: [],
        }
    },
    computed: {
        loading() {
            return this.$store.state.loading.states.main;
        },
    },
    watch: {
        selectedCenters (newCenters, oldCenters) {
            this.user.center_ids = newCenters.map(center => center.id)
        }
    },
    beforeMount() {
        this.$store.commit('resetLoading');
    },
    mounted() {
        this.getUserRoles();
        this.getProfiles();
        this.getCenters();
    },
    methods: {
        async saveUser() {
            const self = this;
            self.$store.commit('loadSections');
            self.user.phones = [...await self.$refs.phones.savePhones()];
            self.user.emails = [...await self.$refs.emails.saveEmails()];
            self.user.email_ids = self.$refs.emails.getEmailIDs();
            axios.post('api/dashboard/admin/users', self.user).then(function (response) {
                self.$store.commit('unloadSections');
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/users/' + response.data.user.id + '/edit');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getUserRoles() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/users/user-roles').then(function (response) {
                self.userRoles = response.data;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getProfiles() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/profiles').then(function (response) {
                self.profileList = response.data;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getCenters() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/centers/list', {
                params: {
                    include: ["org", "phones"]
                }
            }).then(function (response) {
                self.centerList = response.data.centers;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        }
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
